import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import PropTypes from 'prop-types';

import Link from '../Link';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';
import { shadow } from '../../styles/box';

import { ReactComponent as Caret } from '../../images/icons/caret-down.svg';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  position: relative;
  display: inline-flex;
`;

const ButtonLink = styled(Link)`
  position: relative;
  background-color: ${colors.blue['500']};
  color: ${colors.white};
  padding: ${rem(16)} ${rem(10)} ${rem(16)} ${rem(24)};
  border-radius: ${rem(40)} 0 0 ${rem(40)};
  font-weight: ${fontWeights.semiBold};
  font-size: 1rem;
  line-height: 1.2;

  &::after {
    content: '';
    position: absolute;
    top: 20%;
    right: 0;
    width: 1px;
    height: 60%;
    background-color: white;
  }
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  background-color: ${colors.blue['500']};
  padding: ${rem(16)} ${rem(24)} ${rem(16)} ${rem(10)};
  border: none;
  border-radius: 0px ${rem(40)} ${rem(40)} 0px;
  color: ${colors.white};
  font-weight: ${fontWeights.semiBold};
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
`;

const DropdownText = styled.div`
  margin-right: ${rem(10)};
`;

const StyledCaret = styled(Caret)`
  ${({ open }) => (open && css`
    transform: rotate(-180deg);
  `)}
`;

const Menu = styled.ul`
  ${shadow}
  position: relative;
  visibility: hidden;
  opacity: 0;
  border-radius: ${rem(10)};
  background-color: ${colors.white};
  padding: ${rem(20)};
  list-style: none;
  position: absolute;
  margin-top: 20px;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 200ms ease;


  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 15px solid ${colors.white};
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-color: transparent;
  }

  ${({ open }) => (open && css`
    visibility: visible;
    opacity: 1;
  `)}
`;

const MenuItem = styled.li`
  font-weight: ${fontWeights.regular};
  text-align: left;
  color: ${colors.gray['600']};
`;

const MenuLink = styled(Link)`
  display: block;
  padding: ${rem(4)};
  color: inherit;

  &:focus,
  &:hover,
  &:active {
    color: ${colors.black};
    text-decoration: underline;
  }
`;

const ButtonDropdown = ({
  className,
  buttonText,
  buttonLink,
  dropdownText,
  dropdownItems
}) => {
  const { buttonProps, itemProps, isOpen } = useDropdownMenu(dropdownItems.length);

  return (
    <Container className={className}>
      <ButtonLink to={buttonLink}>
        <TextRenderer text={buttonText} />
      </ButtonLink>
      <Dropdown>
        <DropdownButton
          type="button"
          onKeyDown={buttonProps.onKeyDown}
          onClick={buttonProps.onClick}
          tabIndex={buttonProps.tabIndex}
          ref={buttonProps.ref}
          aria-haspopup={buttonProps['aria-haspopup']}
          aria-expanded={buttonProps['aria-expanded']}
        >
          <DropdownText>
            <TextRenderer text={dropdownText} />
          </DropdownText>
          <StyledCaret
            open={isOpen}
          />
        </DropdownButton>
        <Menu
          open={isOpen}
          role="menu"
        >
          {dropdownItems.map(({ text, link }, index) => (
            <MenuItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              <MenuLink
                role="menuitem"
                onKeyDown={itemProps[index].onKeyDown}
                tabIndex={itemProps[index].tabIndex}
                ref={itemProps[index].ref}
                to={link}
              >
                <TextRenderer text={text} />
              </MenuLink>
            </MenuItem>
          ))}
        </Menu>
      </Dropdown>
    </Container>
  );
};

ButtonDropdown.propTypes = {
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  dropdownText: PropTypes.string,
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string
    })
  )
};

export default styled(ButtonDropdown)();
